import { FC } from 'react'
import { Info, PlayCircle, Download, Wrench, Construction, FileText, CheckCircle, Building2, Link2 } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAssetData } from '@/hooks/useAssetData'
import { Motion } from '@/components/ui/motion'
import BottomNavigation from '@/components/BottomNavigation'
import Header from '@/components/Header'
import ErrorView from '@/components/ErrorView'
import LoadingView from '@/components/LoadingView'
import EmptyQRView from '@/components/EmptyQRView'
import QRInfoCard from '@/components/ui/QRInfoCard'
import { useAuthStore } from '@/store/auth.store'
import { useUserType } from '@/hooks/useUserType'
import { filterVisibleSectionsRecursive } from '@/utils/visibility'
import { getAssetValue } from '@/utils/assetValues'

import { useTranslation } from '@/hooks/useTranslation'
import { config } from '@/lib/config'

const Dashboard: FC = () => {
  const navigate = useNavigate()
  const { uuid } = useParams<{ uuid: string }>()
  const { asset: data, isLoading, error, project, uuid: resolvedUuid, assetCore, isProjectInstaller } = useAssetData(uuid, { autoNavigateToLinking: true })
  const { t } = useTranslation()
  const { isAuthenticated } = useAuthStore()
  const { userType } = useUserType()

  const positionNumber = getAssetValue(data, '#/values/positionInfo/number')

  // Create header title with UUID/identification and window room number if available
  const getHeaderTitle = (): string => {
    const identifier = data?.getIdentifier() || resolvedUuid || uuid || ''

    if (positionNumber) {
      return `${t('LINKING_POSITION_NUMBER')} n. ${positionNumber} - ${identifier}`
    }

    return identifier
  }

  if (isLoading && !data) {
    return <LoadingView message={t('LOADING_DASHBOARD')} />
  }

  if (error) {
    return <ErrorView message={error} />
  }

  if (!data || Object.keys(data.nodes).length === 0) {
    return <EmptyQRView uuid={resolvedUuid} project={project} />
  }

  const getIconComponent = (iconName: string, isWizard?: boolean) => {
    if (isWizard) return <PlayCircle className='h-5 w-5 text-[var(--brand-red)]' />

    switch (iconName) {
      case 'info':
        return <Info className='h-5 w-5 text-[var(--brand-red)]' />
      case 'download':
        return <Download className='h-5 w-5 text-[var(--brand-red)]' />
      case 'build':
        return <Wrench className='h-5 w-5 text-[var(--brand-red)]' />
      case 'construction':
        return <Construction className='h-5 w-5 text-[var(--brand-red)]' />
      case 'description':
        return <FileText className='h-5 w-5 text-[var(--brand-red)]' />
      case 'verified':
        return <CheckCircle className='h-5 w-5 text-[var(--brand-red)]' />
      default:
        return <PlayCircle className='h-5 w-5 text-[var(--brand-red)]' />
    }
  }

  // Filter nodes and their children recursively based on visibility rules
  const visibleNodes = filterVisibleSectionsRecursive(data.nodes, userType)

  // Check if there's a qr-info node to display the QRInfoCard
  const hasQrInfoNode = Object.values(visibleNodes).some((item) => {
    const itemWithType = item as unknown as { type?: string; id?: string }
    return itemWithType.type === 'qr-info' || itemWithType.id === 'qr-info'
  })

  // Filter out qr-info nodes from regular items since they're handled by QRInfoCard
  const regularItems = Object.entries(visibleNodes).filter(([_, item]) => {
    const itemWithType = item as unknown as { type?: string; id?: string }
    return !item.isWizard && !(itemWithType.type === 'qr-info' || itemWithType.id === 'qr-info')
  })
  const wizardItems = Object.entries(visibleNodes).filter(([_, item]) => item.isWizard)

  // Check if asset is not linked to a project position (for installer linking button)
  const isAssetNotLinkedToPosition = project && (assetCore?.status === 'empty' || assetCore?.status === 'initialized')

  const handleLinkingClick = () => {
    if (project?.uuid && resolvedUuid) {
      // Navigate to /linking/projectId with equipment UUID as query parameter
      navigate(`/linking/${project.uuid}?qrTagId=${resolvedUuid}`)
    }
  }

  return (
    <div className='min-h-screen bg-[hsl(var(--background))]'>
      {/* Back to the window room list of the project this QR belongs to */}
      <Header
        title={getHeaderTitle()}
        backTo={project?.uuid ? `/project/${project.uuid}` : `/project/from-equipment/${resolvedUuid || uuid}`}
      />

      <div className='container mx-auto px-4 py-8'>
        {/* Manage Linkings Button - Only visible to project installers when asset is not linked to a position and feature is enabled */}
        {config.showLinkManagement && isProjectInstaller && isAssetNotLinkedToPosition && (
          <Motion variant='slideUp' duration={300} delay={100}>
            <div className='mb-6'>
              <div
                className='rounded-xl bg-[hsl(var(--primary))] text-white hover:scale-[1.02] transition-all duration-200 cursor-pointer shadow-md'
                onClick={handleLinkingClick}
              >
                <div className='p-5'>
                  <div className='flex items-center gap-4'>
                    <Link2 className='h-8 w-8' />
                    <div>
                      <h3 className='text-xl font-semibold'>{t('MANAGE_LINKINGS_TITLE')}</h3>
                      <p className='text-sm opacity-90'>{t('MANAGE_LINKINGS_DESCRIPTION')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Motion>
        )}

        {/* QR Information Card - Show only if qr-info node exists */}
        {hasQrInfoNode && (
          <Motion variant='slideUp' duration={300} delay={0}>
            <QRInfoCard
              uuid={resolvedUuid}
              asset={data}
              assetCore={assetCore}
              project={project}
              isLoading={isLoading && !data}
              error={error}
              className="mb-6"
            />
          </Motion>
        )}

        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10 pb-20'>
          {/* Project Dashboard Card - only show if project data exists */}
          {project && config.showProjectCardInDashboard && (
            <Motion variant='slideUp' duration={300} delay={100}>
              <div
                className='rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] cursor-pointer h-40 card-hover'
                onClick={() => navigate(`/project/from-equipment/${resolvedUuid}`)}
              >
                <div className='p-4'>
                  <div className="w-9 h-9 rounded-[10px] bg-[var(--brand-red)]/[0.08] flex items-center justify-center mb-3">
                    <Building2 className='h-5 w-5 text-[var(--brand-red)]' />
                  </div>
                  <h2 className='text-sm font-semibold text-[hsl(var(--foreground))]'>{t('PROJECT_DASHBOARD_TITLE')}</h2>
                  <p className='text-[11px] text-[hsl(var(--muted-foreground))] mt-1'>{t('PROJECT_DASHBOARD_DESCRIPTION')}</p>
                </div>
              </div>
            </Motion>
          )}

          {regularItems.map(([key, item], index) => (
            <Motion key={key} variant='slideUp' duration={300} delay={(project ? index + 2 : index + 1) * 100}>
              <div
                className='rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] cursor-pointer h-40 card-hover'
                onClick={() => navigate(`/view/${uuid}/${key}`)}
              >
                <div className='p-4'>
                  <div className="w-9 h-9 rounded-[10px] bg-[var(--brand-red)]/[0.08] flex items-center justify-center mb-3">{getIconComponent(item.icon)}</div>
                  <h2 className='text-sm font-semibold text-[hsl(var(--foreground))]'>{item.title}</h2>
                  <p className='text-[11px] text-[hsl(var(--muted-foreground))] mt-1'>{item.description}</p>
                </div>
              </div>
            </Motion>
          ))}
        </div>

        {wizardItems.length > 0 && isAuthenticated && isProjectInstaller && (
          <Motion variant='slideDown' duration={500} delay={100}>
            <h2 className='text-2xl font-bold mt-10 mb-4'>Registra installazione</h2>

            <div className='space-y-4'>
              {wizardItems.map(([key, item], index) => (
                <Motion key={key} variant='slideInLeft' duration={300} delay={400 + index * 100}>
                  <div
                    className='rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] cursor-pointer h-32 card-hover'
                    onClick={() => navigate(`/view/${uuid}/wizard/${key}`)}
                  >
                    <div className='p-4 flex flex-row items-center'>
                      <div className='p-2 mr-4'>{getIconComponent(item.icon, true)}</div>
                      <h3 className='text-sm font-semibold text-[hsl(var(--foreground))]'>{item.title}</h3>
                    </div>
                  </div>
                </Motion>
              ))}
            </div>
          </Motion>
        )}
      </div>

      <BottomNavigation projectId={project?.uuid} equipmentUuid={uuid} />
    </div>
  )
}

export default Dashboard
